import React, { useState, useCallback } from 'react';
import { Button, Flexer } from '@ontruck/orc';
import { StyledCard, StyledCardWrapper } from 'modules/assignment-cards/styled';

import DriverSelect from './DriverSelect';
import VehicleSelect from './VehicleSelect';

const t = {
  driver_label: 'Select driver',
  vehicle_label: 'Select vehicle',
  add_driver: 'Add driver',
  cancel: 'Cancel',
};

var ID = function () {
  // Math.random should be unique because of its seeding algorithm.
  // Convert it to base 36 (numbers + letters), and grab the first 9 characters
  // after the decimal.
  return '_' + Math.random().toString(36).substr(2, 9);
};

const voidArr = [];

const isActive = ({ is_active }) => is_active;
const isVehicleType =
  (type) =>
  ({ vehicle_type }) =>
    vehicle_type === type;
const isNotVehicleType = (type) => (v) => !isVehicleType(type)(v);

const DriverForm = ({ onCancelClick, onSubmitClick }) => {
  const [driver, setDriver] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [trailer, setTrailer] = useState(null);
  const [vehicles, setVehicles] = useState(voidArr);

  const _reset = useCallback(() => {
    setDriver(null);
    setVehicles(voidArr);
    setVehicle(null);
    setTrailer(null);
  }, []);

  const handleDriverChange = useCallback(
    (driver) => {
      if (!driver) {
        _reset();
        return;
      }

      const { vehicles } = driver;

      setDriver(driver);
      setVehicles(vehicles || voidArr);
      setVehicle(null);
      setTrailer(null);

      if (vehicles.length === 1) {
        const vehicle = vehicles[0];
        setVehicle(vehicle);
      }
    },
    [_reset]
  );

  const handleVehicleChange = useCallback((vehicle) => {
    if (!vehicle) {
      setVehicle(null);
      return;
    }

    setVehicle(vehicle);
  }, []);

  const handleTrailerChange = useCallback((vehicle) => {
    if (!vehicle) {
      setTrailer(null);
      return;
    }

    setTrailer(vehicle);
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      const { id, contact_name, carrier } = driver;
      onSubmitClick(e, { id: ID(), driver: { id, contact_name }, vehicle, trailer, carrier });
      _reset();
    },
    [onSubmitClick, driver, vehicle, trailer, _reset]
  );

  const vehiclesOptions = vehicles.filter(isActive).filter(isNotVehicleType('trailer'));

  const trailerOptions = vehicles.filter(isActive).filter(isVehicleType('trailer'));

  const vehicleSelectDisabled = !driver;
  const trailerSelectDisabled =
    !driver || !vehicle || (vehicle && vehicle.vehicle_type !== 'tractor_unit');

  const canSubmit =
    driver &&
    vehicle &&
    ((trailer && vehicle.vehicle_type === 'tractor_unit') ||
      (!trailer && vehicle.vehicle_type !== 'tractor_unit'));

  return (
    <StyledCardWrapper>
      <StyledCard data-testid="my_fleet_form" isFirst>
        <Flexer.Parent flexDirection="column" style={{ height: '100%' }} p="XS">
          <Flexer.Child mBottom="XXS">
            <strong>{t.driver_label}</strong>
          </Flexer.Child>
          <Flexer.Child mBottom="S" data-testid="my_fleet_form_driver">
            <DriverSelect
              value={driver}
              onChange={handleDriverChange}
              inputId="my_fleet_driver"
              name="my_fleet_driver"
            />
          </Flexer.Child>
          <Flexer.Child mBottom="XXS">
            <strong>{t.vehicle_label}</strong>
          </Flexer.Child>
          <Flexer.Parent>
            <Flexer.Child mRight="XS" flex="1" data-testid="my_fleet_form_vehicle">
              <VehicleSelect
                value={vehicle}
                disabled={vehicleSelectDisabled}
                options={vehiclesOptions}
                onChange={handleVehicleChange}
                inputId="my_fleet_vehicle"
                name="my_fleet_vehicle"
              />
            </Flexer.Child>
            <Flexer.Child flex="1" data-testid="my_fleet_form_trailer">
              <VehicleSelect
                value={trailer}
                disabled={trailerSelectDisabled}
                options={trailerOptions}
                onChange={handleTrailerChange}
                inputId="my_fleet_trailer"
                name="my_fleet_trailer"
              />
            </Flexer.Child>
          </Flexer.Parent>
          <Flexer.Child flex="1">&nbsp;</Flexer.Child>
          <Flexer.Parent justifyContent="flex-end" mTop="S">
            <Flexer.Child>
              <Button
                text={t.cancel}
                nude
                onClick={onCancelClick}
                data-testid="my_fleet_form_cancel"
              />
            </Flexer.Child>
            <Flexer.Child>
              <Button
                text={t.add_driver}
                onClick={handleSubmit}
                disabled={!canSubmit}
                data-testid="my_fleet_form_submit"
              />
            </Flexer.Child>
          </Flexer.Parent>
        </Flexer.Parent>
      </StyledCard>
    </StyledCardWrapper>
  );
};

export default DriverForm;
